import React, { Context, createContext, PropsWithChildren, useContext } from 'react';
import { Table } from '@tanstack/react-table';
import {
  OnVariablesChanged,
  QuerySettings
} from '../../DataTable-v8/utils/DataTableQueryProvider/DataTableQuery.types';
import * as DataTableTypes from './DataTableTypes';

export interface DataTableContextProps<D extends object> extends Partial<Table<D>> {
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  isFetching: boolean | undefined;
  tableHeight: string | undefined;
  manualSortBy: boolean | undefined;
  setSelectedRows: (rows: D[], pageIndex: string) => void;
  clearAllSelectedRows: () => void;
  selectedRows: D[];
  border: DataTableTypes.DataTableBorder | undefined;
  previousURLKey: string | undefined;
  featureFlags?: DataTableTypes.DataTableFeatureFlags;
  onColumnConfigurationChanged: ((settings: DataTableTypes.ColumnConfiguration) => void) | undefined;
  totalCount: number | undefined;
  querySettings?: QuerySettings;
  resizeTableColumns: boolean;
  onVariablesChanged?: (variables: OnVariablesChanged<any, any>) => void;
}

const DataTableContext = createContext<DataTableContextProps<any>>({} as DataTableContextProps<any>);

export function DataTableProvider<D extends object>({
  children,
  value
}: PropsWithChildren<{ value: DataTableContextProps<D> }>) {
  return <DataTableContext.Provider value={value}>{children}</DataTableContext.Provider>;
}

export const useDataTableContext = <D extends object>(): DataTableContextProps<D> => {
  const context = useContext<DataTableContextProps<D>>(
    DataTableContext as unknown as Context<DataTableContextProps<D>>
  );

  if (!context) {
    throw new Error('useDataTableContext must be used under DataTableProvider');
  }
  return context;
};
